import { I18n } from '@calendly/i18n';
import { Body, EmptyState as EmptyStateContainer, Header, HeaderCell, Table } from '@calendly/table';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText } from '@calendly/ui/components/typography';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { CONTENT_PAGES, useNewEmptyStates } from 'features/empty_state';
import { selectAllManagedEventTypes, useFetchManagedEventTypesQuery } from './api';
import { CreateDropdownButton } from './create_dropdown_button';
import { ManagedEventTypeContextProvider } from './managed_event_types_data';
import { ManagedEventTypesRow } from './managed_event_types_row';
import { useManagedEventTypeQueryParams } from './use_managed_event_type_query_params';
import { EMPTY_STATE_TYPES, EmptyState as NewEmptyState } from '../../components/empty_state';
const noWrapCss = "n1u3m7cb";
const NoWrap = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(noWrapCss, props.className)} />;
});
const emptyStateCreateButtonContainerCss = "etpawe6";
const EmptyStateCreateButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emptyStateCreateButtonContainerCss, props.className)} />;
});
const EmptyState = ({
  disabled
}) => <EmptyStateContainer>
    <BodyText>{I18n.t('publisher.organization.managed_event_types.empty_state.placeholder')}</BodyText>
    <EmptyStateCreateButtonContainer>
      <CreateDropdownButton disabled={disabled} />
    </EmptyStateCreateButtonContainer>
  </EmptyStateContainer>;
const EmptySearchState = () => <EmptyStateContainer>
    <BodyText weight="bold">{I18n.t('publisher.organization.managed_event_types.table.empty.search')}</BodyText>
    <BodyText>{I18n.t('publisher.organization.managed_event_types.table.empty.search_note_active')}</BodyText>
  </EmptyStateContainer>;
export const ManagedEventTypesTable = ({
  disabled
}) => {
  const [managedEventsQueryParams, updateQueryParams] = useManagedEventTypeQueryParams();
  const {
    data,
    isSuccess,
    isFetching
  } = useFetchManagedEventTypesQuery({
    ...managedEventsQueryParams
  });
  const isNewEmptyStateEnabled = useNewEmptyStates();
  const {
    filterTerm,
    sortField,
    sortOrder
  } = managedEventsQueryParams;
  const managedEventTypes = data && selectAllManagedEventTypes(data.collection);
  const isSearchEmpty = filterTerm && managedEventTypes?.length === 0;
  const isEmpty = !filterTerm && managedEventTypes?.length === 0;
  const sortingHandler = column => {
    updateQueryParams({
      filterTerm,
      sortField: column,
      sortOrder: !sortOrder || sortOrder === 'asc' ? 'desc' : 'asc'
    });
  };
  if (!isFetching && isSuccess && isEmpty && isNewEmptyStateEnabled) {
    return <NewEmptyState type={EMPTY_STATE_TYPES.centered} contentKey={CONTENT_PAGES.managedEventTypes} featureKey="managed_events" options={{
      url: '/app/admin/managed_event_types/new'
    }} ButtonOverride={CreateDropdownButton} />;
  }
  return <div data-component="managed-event-types">
      <Table disabled={disabled} sortedColumn={{
      name: sortField,
      direction: sortOrder
    }} onSort={sortingHandler}>
        <Header>
          <HeaderCell columnName="name">
            <NoWrap>{I18n.t('publisher.organization.managed_event_types.table.headers.name')}</NoWrap>
          </HeaderCell>
          <HeaderCell>
            <NoWrap>{I18n.t('publisher.organization.managed_event_types.table.headers.type')}</NoWrap>
          </HeaderCell>
          <HeaderCell>
            <NoWrap>{I18n.t('publisher.organization.managed_event_types.owned_by')}</NoWrap>
          </HeaderCell>
          <HeaderCell>
            <NoWrap>{I18n.t('publisher.organization.managed_event_types.table.headers.assigned_to')}</NoWrap>
          </HeaderCell>
          <HeaderCell columnName="updated_at">
            <NoWrap>{I18n.t('publisher.organization.managed_event_types.table.headers.date_last_edited')}</NoWrap>
          </HeaderCell>
          <HeaderCell hugContent>
            <VisuallyHidden>{I18n.t('publisher.components.table.action_menu')}</VisuallyHidden>
          </HeaderCell>
        </Header>
        <Body>
          {isFetching ? <EmptyStateContainer>
              <Loader data-testid="loader" />
            </EmptyStateContainer> : managedEventTypes.map(eventType => <ManagedEventTypeContextProvider value={{
          id: eventType.id
        }} key={eventType.id}>
              <ManagedEventTypesRow disabled={disabled} />
            </ManagedEventTypeContextProvider>)}
          {!isFetching && isSuccess && isEmpty && <EmptyState disabled={disabled} />}
          {!isFetching && isSuccess && isSearchEmpty && <EmptySearchState />}
        </Body>
      </Table>
    </div>;
};

require("./managed_event_types_table.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./managed_event_types_table.js");